import {
  getCustomerDrawerOptions,
  getExecutionStrategiesDrawerOptions,
  getOrdTypeDrawerOptions,
  getTimeDrawerOptions,
  getTimeInForceDrawerOptions,
  getWorkflowDrawerOptions,
  transformDrawerValueToNumber,
  type ColumnDef,
  type Customer,
  type ICustomerOrderExecutionRule,
  type InputsAndDropdownsDrawerOption,
  type Security,
} from '@talos/kyoko';
import { isActiveRuleColumn } from '../columns';

export function getCustomerOrderAcceptanceRulesDrawerOptions({
  customers,
  securitiesBySymbol,
}: {
  customers: Customer[];
  securitiesBySymbol: Map<string, Security>;
}): InputsAndDropdownsDrawerOption<ICustomerOrderExecutionRule>[] {
  return [
    {
      field: 'Counterparty',
      type: 'dropdown',
      title: 'Counterparty',
      placeholder: '*',
      getDropdownOptions: () => getCustomerDrawerOptions(customers),
    },
    {
      field: 'Symbol',
      type: 'symbolSelector',
      title: 'Symbol',
      placeholder: '*',
    },
    {
      field: 'ExecutionStrategy',
      type: 'dropdown',
      title: 'Execution Strategy',
      guideText:
        'How the dealer executes the customer order. "Riskless Principal" hedges via a cover-and-deal strategy; "Order Acceptance" passes the order unhedged.',
      getIsRequired: () => true,
      getDropdownOptions: rule => getExecutionStrategiesDrawerOptions(rule.TimeInForce),
    },
    { type: 'divider' },
    {
      field: 'TimeInForce',
      type: 'dropdown',
      title: 'Time In Force',
      guideText:
        'Determines how long the order remains active. "Fill or Kill" must be fully filled immediately or cancelled; "Good Till Cancel" remains open until filled or manually cancelled.',
      getDropdownOptions: rule => getTimeInForceDrawerOptions(rule.ExecutionStrategy),
    },
    {
      field: 'Priority',
      type: 'inputNumeric',
      title: 'Priority',
      guideText:
        "Rule evaluation priority. A lower value means higher priority. Ties are broken by the rule's creation timestamp.",
      transformControlToDrawerRecord: transformDrawerValueToNumber,
    },
    {
      field: 'StartTime',
      type: 'dropdown',
      title: 'Start Time',
      guideText: 'Time of day (UTC) when this rule becomes active, in HH:MM:SS format.',
      getDropdownOptions: () => getTimeDrawerOptions(),
    },
    {
      field: 'EndTime',
      type: 'dropdown',
      title: 'End Time',
      guideText: 'Time of day (UTC) when this rule deactivates, in HH:MM:SS format.',
      getDropdownOptions: () => getTimeDrawerOptions(),
    },
    {
      field: 'OrdType',
      type: 'dropdown',
      title: 'Order Type',
      guideText:
        'Whether the customer order is treated as a Limit order (with a specified price) or a Market order (fills at best available price).',
      getDropdownOptions: () => getOrdTypeDrawerOptions(),
    },
    {
      field: 'Workflow',
      type: 'dropdown',
      title: 'Workflow',
      guideText:
        'The order workflow this rule applies to, e.g. Whitelabel RFQ, Customer API Order, Sales RFQ. Leave blank to match all workflows.',
      getDropdownOptions: () => getWorkflowDrawerOptions(),
    },
    {
      field: 'MinQuantityThreshold',
      type: 'input',
      title: 'Min Quantity Threshold',
      guideText: 'Minimum order size (in base currency) for this rule to apply.',
      getInputSuffix: rule => securitiesBySymbol.get(rule.Symbol)?.BaseCurrency ?? '',
      getIsDisabled: rule => !rule.Symbol,
    },
    {
      field: 'MaxQuantityThreshold',
      type: 'input',
      title: 'Max Quantity Threshold',
      guideText: 'Maximum order size (in base currency) for this rule to apply.',
      getInputSuffix: rule => securitiesBySymbol.get(rule.Symbol)?.BaseCurrency ?? '',
      getIsDisabled: rule => !rule.Symbol,
    },
    {
      field: 'MinNotionalThreshold',
      type: 'input',
      title: 'Min Notional Threshold',
      guideText: 'Minimum order notional value (in quote currency) for this rule to apply.',
      getInputSuffix: rule => securitiesBySymbol.get(rule.Symbol)?.QuoteCurrency ?? '',
      getIsDisabled: rule => !rule.Symbol,
    },
    {
      field: 'MaxNotionalThreshold',
      type: 'input',
      title: 'Max Notional Threshold',
      guideText: 'Maximum order notional value (in quote currency) for this rule to apply.',
      getInputSuffix: rule => securitiesBySymbol.get(rule.Symbol)?.QuoteCurrency ?? '',
      getIsDisabled: rule => !rule.Symbol,
    },
  ];
}

export const CUSTOMER_ORDER_ACCEPTANCE_RULES_COLUMNS: ColumnDef<ICustomerOrderExecutionRule>[] = [
  isActiveRuleColumn,
  {
    type: 'counterparty',
    field: 'Counterparty',
    title: 'Counterparty',
    initialWidth: 170,
    sortable: true,
    initialSort: '+',
    description: 'Counterparty this rule applies to',
    params: { showAsteriskOnEmpty: true },
  },
  {
    type: 'security',
    field: 'Symbol',
    initialWidth: 170,
    sortable: true,
    initialSort: '-',
    params: { showAsteriskOnEmpty: true },
  },
  {
    type: 'prettyText',
    field: 'ExecutionStrategy',
    initialWidth: 170,
    description:
      'OrderAcceptance means orders matching this rule will not be executed in the market before being confirmed to the client.',
  },
  {
    type: 'prettyText',
    field: 'TimeInForce',
    initialWidth: 130,
  },
  {
    type: 'prettyText',
    field: 'OrdType',
    initialWidth: 130,
  },
  {
    type: 'prettyText',
    field: 'Workflow',
    initialWidth: 180,
    initialHide: true,
  },
  {
    type: 'number',
    field: 'Priority',
    initialWidth: 120,
    sortable: true,
    sort: '-',
    description:
      'If multiple rules with the same granularity apply to an order, priority order will determine which takes precedence',
  },
  { type: 'time', field: 'StartTime', initialWidth: 150 },
  { type: 'time', field: 'EndTime', initialWidth: 150 },
  {
    type: 'size',
    field: 'MinQuantityThreshold',
    initialWidth: 150,
    description: `Minimum quantity (in units of base currency) this rule will apply to`,
    initialHide: true,
    params: { securityField: 'Symbol', securityFieldProperty: 'BaseCurrency' },
  },
  {
    type: 'size',
    field: 'MaxQuantityThreshold',
    initialWidth: 150,
    description: `Maximum quantity (in units of base currency) this rule will apply to`,
    initialHide: true,
    params: { securityField: 'Symbol', securityFieldProperty: 'BaseCurrency' },
  },
  {
    type: 'size',
    field: 'MinNotionalThreshold',
    initialWidth: 175,
    description: `Minimum amount (in units of quote currency) this rule will apply to`,
    initialHide: true,
    params: { securityField: 'Symbol', securityFieldProperty: 'QuoteCurrency' },
  },
  {
    type: 'size',
    field: 'MaxNotionalThreshold',
    initialWidth: 175,
    description: 'Maximum amount (in units of quote currency) this rule will apply to',
    initialHide: true,
    params: { securityField: 'Symbol', securityFieldProperty: 'QuoteCurrency' },
  },
];

export const CUSTOMER_ORDER_ACCEPTANCE_RULES_BULK_EDIT_FIELDS: (keyof ICustomerOrderExecutionRule)[] = [
  'ExecutionStrategy',
  'TimeInForce',
  'Priority',
  'StartTime',
  'EndTime',
  'OrdType',
  'Workflow',
  'MinNotionalThreshold',
  'MaxNotionalThreshold',
];
